<template>
  <div id="register-tab">
    <Message
      v-if="success"
      severity="success"
      :closable="false"
    >
      {{ isAutoApproval ? $t('registerpage.messages.success') : $t('registerpage.messages.approval') }}
    </Message>

    <form
      v-if="!success"
      @submit.prevent="register"
    >
      <p><strong class="required-input">{{ $t('registerpage.fieldset.stakeholder') }}</strong></p>

      <div class="p-input mb-3">
        <InputText
          id="stakeholder"
          v-model.trim="v$.stakeholderId.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.stakeholderId.$error }"
          type="text"
          autocomplete="on"
          required
        />
        <div
          v-for="error of v$.stakeholderId.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <p class="mt-5">
        <strong>{{ $t('registerpage.fieldset.contact') }}</strong>
      </p>

      <div class="grid">
        <div class="col">
          <div class="p-input mb-3">
            <label
              for="lastname"
              class="required-input"
            >{{ $t('registerpage.input.lastname') }}</label>
            <InputText
              id="lastname"
              v-model="v$.lastname.$model"
              class="col-12 mt-2"
              :class="{'invalid-input': v$.lastname.$error }"
              type="text"
              autocomplete="family-name"
              required
            />
            <div
              v-for="error of v$.lastname.$errors"
              :key="error.$uid"
              class="input-errors"
            >
              <div class="error-msg">
                {{ error.$message }}
              </div>
            </div>
          </div>
        </div>

        <div class="col">
          <div class="p-input mb-3">
            <label
              for="firstname"
              class="required-input"
            >{{ $t('registerpage.input.firstname') }}</label>
            <InputText
              id="firstname"
              v-model="v$.firstname.$model"
              class="col-12 mt-2"
              :class="{'invalid-input': v$.firstname.$error }"
              type="text"
              autocomplete="given-name"
              required
            />
            <div
              v-for="error of v$.firstname.$errors"
              :key="error.$uid"
              class="input-errors"
            >
              <div class="error-msg">
                {{ error.$message }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="company"
          class="required-input"
        >{{ $t('registerpage.input.company') }}</label>
        <InputText
          id="company"
          v-model="v$.company.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.company.$error }"
          type="text"
          autocomplete="organization"
          required
        />
        <div
          v-for="error of v$.company.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="email-register"
          class="required-input"
        >{{ $t('registerpage.input.email') }}</label>
        <InputText
          id="email-register"
          v-model="v$.email.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.email.$error }"
          type="email"
          autocomplete="email"
          required
        />
        <div
          v-for="error of v$.email.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="email-confirmation"
          class="required-input"
        >{{ $t('registerpage.input.emailConfirmation') }}</label>
        <InputText
          id="email-confirmation"
          v-model="v$.emailConfirmation.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.emailConfirmation.$error }"
          type="email"
          autocomplete="email"
          required
        />
        <div
          v-for="error of v$.emailConfirmation.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label for="phoneNumber">{{ $t('registerpage.input.phoneNumber') }}</label>
        <InputPhoneNumber
          id="phoneNumber"
          v-model:phoneNumberCountryCode="v$.phoneNumberCountryCode.$model"
          v-model:phoneNumber="v$.phoneNumber.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.phoneNumber.$error }"
        />
        <div
          v-for="error of v$.phoneNumber.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="password-register"
          class="required-input"
        >{{ $t('registerpage.input.password') }}</label>
        <Password
          id="password-register"
          v-model="v$.password.$model"
          class="col-12 p-0 mt-2"
          :class="{'invalid-input': v$.password.$error }"
          :toggle-mask="true"
          :feedback="false"
          autocomplete="new-password"
          required
        />
        <div
          v-for="error of v$.password.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
            <p v-if="error.$validator === 'passwordPattern'">
              {{ $t('validations.passwordSpecialCharacter') }}: !"#$%&'()*+,-./:;&lt;\?@[]^_
            </p>
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="password-confirmation"
          class="required-input"
        >{{ $t('registerpage.input.confirm_password')
        }}</label>
        <pre>{{ v$.passWordConfirmation }}</pre>
        <Password
          id="password-confirmation"
          v-model="v$.passwordConfirmation.$model"
          class="col-12 p-0 mt-2"
          :class="{'invalid-input': v$.passwordConfirmation.$error }"
          :toggle-mask="true"
          :feedback="false"
          autocomplete="new-password"
          required
        />
        <div
          v-for="error of v$.passwordConfirmation.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <captcha-component
        ref="captcha"
        v-model:answer="captcha.answer"
        v-model:session="captcha.session"
      />


      <Message
        v-if="errors.length > 0"
        severity="error"
        :closable="false"
      >
        <ul class="list-none m-0 p-0">
          <li
            v-for="error of errors"
            :key="error"
          >
            {{ $t(error) }}
          </li>
        </ul>
      </Message>

      <div class="text-right mt-3">
        <div
          v-if="isSaving"
          class="inline-block mr-4"
        >
          <div class="loader-dots">
            <div />
            <div />
            <div />
          </div>
        </div>

        <Button
          type="submit"
          class="p-button-rounded p-button-outlined"
          :disabled="registerButtonDisabled"
        >
          {{ $t('registerpage.input.register') }}
        </Button>
      </div>
    </form>
  </div>
</template>

<script>
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Message from 'primevue/message';
import Password from 'primevue/password'
import {useVuelidate} from '@vuelidate/core';
import {email, passwordPattern, phoneNumberPattern, required, sameAs} from '@/i18n/i18nValidators';
import CaptchaComponent from "@/components/CaptchaComponent.vue";
import InputPhoneNumber from "@/components/InputPhoneNumber.vue";

export default {
  name: 'LoginForm',
  components: {InputPhoneNumber, CaptchaComponent, InputText, Button, Message, Password },
  setup() {
    return {
      v$: useVuelidate()
    }
  },
  data() {
    return {
      stakeholderId: '',
      lastname: '',
      firstname: '',
      company: '',
      email: '',
      emailConfirmation: '',
      phoneNumberCountryCode: 'FR',
      phoneNumber: '',
      password: '',
      passwordConfirmation: '',
      errors: [],
      success: false,
      isSaving: false,
      captcha: {
        answer: null,
        session: null
      }
    }

  },
  validations() {
    return {
      stakeholderId: { required },
      lastname: { required },
      firstname: { required },
      company: { required },
      email: { required, email },
      emailConfirmation: {
        required, email,
        sameAsEmail: sameAs(this.email),
      },
      phoneNumberCountryCode: {},
      phoneNumber: { phoneNumberPattern: phoneNumberPattern(this.phoneNumberCountryCode) },
      password: { required, passwordPattern },
      passwordConfirmation: {
        required, passwordPattern,
        sameAsPassword: sameAs(this.password),
      }
    }
  },
  computed: {
    registerButtonDisabled() {
      return this.v$.$invalid || this.isSaving || !this.captcha.answer || !this.captcha.session;
    },
    isAutoApproval() {
      return this.$store.getters['setting/isAutoApproval'];
    }
  },

  methods: {
    async register() {
      this.errors = [];
      const isFormValid = await this.v$.$validate();
      if (!isFormValid) return;

      this.isSaving = true;

      const data = {
        stakeholderId: this.stakeholderId,
        lastname: this.lastname,
        firstname: this.firstname,
        company: this.company,
        email: this.email,
        phoneNumberCountryCode: this.phoneNumberCountryCode,
        phoneNumber: this.phoneNumber,
        password: this.password,
        captchaSession: this.captcha.session,
        captchaAnswer: this.captcha.answer,
      }
      await this.$userService.registerUser(data)
        .then(() => {
          this.success = true;
          this.errors = [];
          this.isSaving = false;
          document.getElementById("register-tab").scrollIntoView();
        })
        .catch(e => {
          this.captcha.answer = null;
          this.captcha.session = null;
          this.success = false;
          this.isCaptchaExist = false
          this.$refs.captcha.reload();
          this.errors = e.response.data;
          this.isSaving = false;
        });
    },
  }
}
</script>

<style scoped lang="scss">

</style>
